import axiosInstance from "../interceptor/interceptor";
import eligibilityCheckDictionary from "../dictionary/eligibilityCheckDictionary";
import type { EncryptedPayload } from "../../../utils/crypto";

// Body interfaces for requests
export interface GetUniversitiesBody {
  universityName?: string;
  countryId: string;
}

export interface GetInstitutesBody {
  instituteName?: string;
  universityId: string;
  countryId: string;
}

export interface GetCoursesBody {
  courseName?: string;
  instituteId: string;
}

/** Plain body or hybrid-encrypted payload from `encryptData` (PII mobile). */
export type DedupeCheckBody =
  | EncryptedPayload
  | { mobileNumber: string; courseName: string };

// Cities-by-country-state is requested independently by several places
// (useApplicantMasterData, and direct callers in FormEdit/DynamicForm/
// AddNewCoApplicantModal) and can be asked for the same
// country/state more than once (e.g. once per co-applicant tab) before the
// first call resolves — cache the in-flight/resolved promise per
// country+state key so the underlying API is only actually hit once.
const citiesByCountryStatePromises: Record<string, Promise<any>> = {};

const eligibilityCheckServices = {
  getForm: (body: any) =>
    axiosInstance.post(eligibilityCheckDictionary.getForm(), body),
  getCourses: (body: GetCoursesBody) =>
    axiosInstance.post(eligibilityCheckDictionary.getCourses(), body),
  getCountries: () =>
    axiosInstance.get(eligibilityCheckDictionary.getCountries()),
  getUniversities: (body: GetUniversitiesBody) =>
    axiosInstance.post(eligibilityCheckDictionary.getUniversities(), body),
  getInstitutes: (body: GetInstitutesBody) =>
    axiosInstance.post(eligibilityCheckDictionary.getInstitutes(), body),
  getSalutations: () =>
    axiosInstance.get(eligibilityCheckDictionary.getSalutations()),
  getSystemValues: () =>
    axiosInstance.get(eligibilityCheckDictionary.getSystemValues()),
  getMaritalStatusMaster: () =>
    axiosInstance.get(eligibilityCheckDictionary.getMaritalStatusMaster()),
  getNationalityMaster: () =>
    axiosInstance.get(eligibilityCheckDictionary.getNationalityMaster()),
  getDesignations: () =>
    axiosInstance.get(eligibilityCheckDictionary.getDesignations()),
  getProducts: () =>
    axiosInstance.post(eligibilityCheckDictionary.getProducts()),
  getMasters: () => axiosInstance.post(eligibilityCheckDictionary.getMasters()),
  getApplicantDetails: (payload: any) =>
    axiosInstance.get(eligibilityCheckDictionary.getApplicantDetails(payload)),
  dedupeCheck: (payload: DedupeCheckBody) =>
    axiosInstance.post(eligibilityCheckDictionary.dedupeCheck(), payload),
  captureFormData: (payload: { formData: any; submissionTrackingId: string }) =>
    axiosInstance.post(eligibilityCheckDictionary.captureFormData(), payload),
  sendOtp: (payload: {
    name: string;
    mobileNumber?: string;
    email?: string;
    isResend?: boolean;
  }) => axiosInstance.post(eligibilityCheckDictionary.sendOtp(), payload),
  checkConsent: (data: string) =>
    axiosInstance.post(eligibilityCheckDictionary.checkConsent(), { data }),
  sendConsentOtp: (data: string) =>
    axiosInstance.post(eligibilityCheckDictionary.sendConsentOtp(), { data }),
  resendConsentOtp: (data: string, channel: "sms" | "email") =>
    axiosInstance.post(eligibilityCheckDictionary.resendConsentOtp(), { data, channel }),
  verifyConsentOtp: (payload: { data: string; mobileOTP?: string; emailOTP?: string }) =>
    axiosInstance.post(eligibilityCheckDictionary.verifyConsentOtp(), payload),
  verifyOtp: (payload: {
    mobileNumber?: string;
    email?: string;
    key: string;
    mobileOtp?: string;
    mobileOtpCrtdAt?: string;
    emailOtp?: string;
    emailOtpCrtdAt?: string;
    type: "mobile" | "email";
    trackingId?: string;
    userType?: string
  }) => {
    const { type, ...rest } = payload;
    return axiosInstance.post(eligibilityCheckDictionary.verifyOtp(type), rest);
  },
  checkOffer: (payload: any) =>
    axiosInstance.post(eligibilityCheckDictionary.checkOffer(), payload),
  createPartnerLead: (payload: any) =>
    axiosInstance.post(eligibilityCheckDictionary.createPartnerLead(), payload),
  leadAllocation: (payload: any) =>
    axiosInstance.post(eligibilityCheckDictionary.leadAllocation(), payload),
  getDocumentCategoriesApi: () =>
    axiosInstance.get(eligibilityCheckDictionary.getDocumentCategories()),
  partnerGetLeadDetailsApi: (payload: any) =>
    axiosInstance.post(
      eligibilityCheckDictionary.partnerGetLeadDetails(),
      payload
    ),
  getMandatoryDocuments: (payload: any) =>
    axiosInstance.post(
      eligibilityCheckDictionary.getMandatoryDocuments(),
      payload
    ),
  getDocuments: (payload: any) =>
    axiosInstance.post(eligibilityCheckDictionary.getDocuments(), payload),
  getViewMandatoryDocuments: (payload: any) =>
    axiosInstance.get(
      eligibilityCheckDictionary.getViewMandatoryDocuments(payload)
    ),
  uploadDocument: (payload: any) =>
    axiosInstance.post(eligibilityCheckDictionary.uploadDocument(), payload),
  getDocumentList: (payload: any) =>
    axiosInstance.post(eligibilityCheckDictionary.getDocumentList(), payload),

  createOtherDocumentApi: (payload: any) =>
    axiosInstance.post(
      eligibilityCheckDictionary.createOtherDocument(),
      payload
    ),

  previewDocument: (payload: any) =>
    axiosInstance.post(eligibilityCheckDictionary.previewDocument(), payload),
  downloadDocument: (payload: any) =>
    axiosInstance.post(eligibilityCheckDictionary.downloadDocument(), payload),
  previewAggregateDocument: (payload: any) =>
    axiosInstance.post(
      eligibilityCheckDictionary.previewAggregateDocument(),
      payload
    ),

  addMoreDocument: (payload: any) =>
    axiosInstance.post(eligibilityCheckDictionary.addMoreDocument(), payload),
  getViewBulkDocuments: (trackingId: any) =>
    axiosInstance.get(
      eligibilityCheckDictionary.getViewBulkDocuments(trackingId)
    ),
  uploadBulkDocuments: (payload: any) =>
    axiosInstance.post(
      eligibilityCheckDictionary.uploadBulkDocuments(),
      payload
    ),
  deleteUserDocument: (payload: any) =>
    axiosInstance.post(
      eligibilityCheckDictionary.deleteUserDocument(),
      payload
    ),
  triggerLinkToCollectDocuments: (payload: any) =>
    axiosInstance.post(
      eligibilityCheckDictionary.triggerLinkToCollectDocuments(),
      payload
    ),
  accountAggregatorRequest: (payload: any) =>
    axiosInstance.post(
      eligibilityCheckDictionary.accountAggregatorRequest(),
      payload
    ),
  accountAggregatorRequestConsent: (payload: any) =>
    axiosInstance.post(
      eligibilityCheckDictionary.accountAggregatorRequestConsent(),
      payload
    ),

  fetchBreApi: (payload: any) =>
    axiosInstance.post(eligibilityCheckDictionary.fetchBre(), payload),

  checkOfferProvisional: (payload: any) =>
    axiosInstance.post(
      eligibilityCheckDictionary.checkOfferProvisional(),
      payload
    ),

  getPinCode: (zipCode: string) =>
    axiosInstance.get(eligibilityCheckDictionary.getPinCode(zipCode)),
  getZipCode: (zipCode: string) =>
    axiosInstance.get(eligibilityCheckDictionary.getZipCode(zipCode)),
  getState: (stateCode: string) =>
    axiosInstance.get(eligibilityCheckDictionary.getState(stateCode)),
  getStatesByCountry: (countryName: string) =>
    axiosInstance.get(eligibilityCheckDictionary.getStatesByCountry(countryName)),
  getCitiesByCountryState: (countryName: string, stateName?: string) => {
    const cacheKey = `${String(countryName || "").trim().toLowerCase()}|${String(
      stateName || ""
    ).trim().toLowerCase()}`;
    if (!citiesByCountryStatePromises[cacheKey]) {
      citiesByCountryStatePromises[cacheKey] = axiosInstance
        .get(
          eligibilityCheckDictionary.getCitiesByCountryState(countryName, stateName)
        )
        .catch((error) => {
          delete citiesByCountryStatePromises[cacheKey];
          throw error;
        });
    }
    return citiesByCountryStatePromises[cacheKey];
  },
  getCity: (cityCode: string) =>
    axiosInstance.get(eligibilityCheckDictionary.getCity(cityCode)),

  getEntranceExamMaster: () =>
    axiosInstance.get(eligibilityCheckDictionary.getEntranceExamMaster()),
  getAddressTypes: () =>
    axiosInstance.get(eligibilityCheckDictionary.getAddressTypes()),
  getPropertyTypes: () =>
    axiosInstance.get(eligibilityCheckDictionary.getPropertyTypes()),
  getPropertySubTypes: (propertyTypeCode?: string) =>
    axiosInstance.get(eligibilityCheckDictionary.getPropertySubTypes(propertyTypeCode)),
  pushDocument: (payload: any) =>
    axiosInstance.post(eligibilityCheckDictionary.pushDocument(), payload),
  bulkDocumentCount: (payload: { trackingId: string }) =>
    axiosInstance.post(eligibilityCheckDictionary.bulkDocumentCount(), payload),
  updateApplicationStatus: (trackingId: string, payload: { status: string }) =>
    axiosInstance.post(eligibilityCheckDictionary.updateApplicationStatus(trackingId), payload),
  getAAConsentDuration: (trackingId: string) =>
     axiosInstance.get(
      eligibilityCheckDictionary.aaConsentDuration(trackingId)
    ),
  getConsentDurationData: (trackingId: string) =>
    axiosInstance.get(eligibilityCheckDictionary.aaConsentDuration(trackingId)),
  saveConsentDuration: (payload: any) =>
    axiosInstance.post(eligibilityCheckDictionary.saveConsentDuration(), payload),
  consentFIRequest: (payload: any) =>
    axiosInstance.post(eligibilityCheckDictionary.consentFIRequest(), payload),
  getFIRequest: (sessionId: string, applicationNo: string) =>
    axiosInstance.get(eligibilityCheckDictionary.getFIRequest(sessionId, applicationNo)),
  getCollateralIds:(trackingId:string)=>
    axiosInstance.post(eligibilityCheckDictionary.collaterIdGet(trackingId)),
};

export default eligibilityCheckServices;
